import type { ListConfiguration } from '../types/list';

interface CacheEntry {
	config: ListConfiguration;
	timestamp: number;
}

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Centralized service for caching list configurations.
 */
export class ListConfigurationCacheService {
	private static readonly cache: Map<string, CacheEntry> = new Map();
	private static ttl: number = DEFAULT_TTL;

	/**
	 * Get cached configuration.
	 */
	static get(listKey: string, listId: string): ListConfiguration | null {
		const key = this.buildKey(listKey, listId);
		const entry = this.cache.get(key);

		if (entry == null) {
			return null;
		}

		if (Date.now() - entry.timestamp > this.ttl) {
			this.cache.delete(key);

			return null;
		}

		return entry.config;
	}

	/**
	 * Set cached configuration.
	 */
	static set(listKey: string, listId: string, config: ListConfiguration): void {
		const key = this.buildKey(listKey, listId);
		this.cache.set(key, {
			config,
			timestamp: Date.now(),
		});
	}

	/**
	 * Invalidate cache for an listKey.
	 */
	static invalidate(listKey: string): void {
		for (const key of this.cache.keys()) {
			if (key.startsWith(`${listKey}:`)) {
				this.cache.delete(key);
			}
		}
	}

	/**
	 * Clear entire cache.
	 */
	static clear(): void {
		this.cache.clear();
	}

	/**
	 * Set TTL for cache entries.
	 */
	static setTTL(ttlMs: number): void {
		this.ttl = ttlMs;
	}

	private static buildKey(listKey: string, listId: string): string {
		return `${listKey}:${listId}`;
	}
}
